import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout as AntLayout,
  Menu,
  Button,
  Dropdown,
  Space,
  Avatar,
  message,
  Modal,
} from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  HomeOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  GlobalOutlined,
  CodeOutlined,
  UserOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useAppStore } from '../store';
import { apiClient } from '@packages/api-client';
import { useEmbedMode } from '../hooks/useEmbedMode';

const { Header, Content, Footer } = AntLayout;

const Layout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, currentOntology, isEditorDirty, setIsEditorDirty } = useAppStore();
  const { isEmbedMode, shouldShowEmbedHeader, shouldHideHeader } =
    useEmbedMode();

  // 获取当前用户信息
  const currentUser = apiClient.getCurrentUser();

  // 需要显示返回按钮的页面路径数组
  const pagesWithBackButton = [
    '/opm-editor',
    '/opm-editor-legacy',
    '/knowledge-graph',
    '/semantic-data-map',
    '/data-map-editor',
  ];

  // 判断当前页面是否需要显示返回按钮（非首页且在白名单中）
  const shouldShowBackButton =
    location.pathname !== '/' &&
    pagesWithBackButton.includes(location.pathname);

  // 返回上一页（有未保存更改时弹出确认框）
  const handleGoBack = () => {
    if (isEditorDirty) {
      Modal.confirm({
        title: '有未保存的更改',
        content: '您有未保存的更改，离开后将会丢失，是否确认离开？',
        okText: '离开',
        okButtonProps: { danger: true },
        cancelText: '留下',
        onOk: () => {
          setIsEditorDirty(false);
          navigate(-1);
        },
      });
    } else {
      navigate(-1);
    }
  };

  // 返回首页
  const handleGoHome = () => {
    navigate('/');
  };

  // 菜单项配置
  const menuItems: MenuProps['items'] = [
    {
      key: '/',
      label: t('nav.home'),
      icon: <HomeOutlined />,
    },
    // {
    //   key: '/about',
    //   label: t('nav.about'),
    //   icon: <InfoCircleOutlined />,
    // },
    // {
    //   key: '/settings',
    //   label: t('nav.settings'),
    //   icon: <SettingOutlined />,
    // },
    {
      key: '/opm-editor',
      label: 'OPM 编辑器',
      icon: <CodeOutlined />,
    },
  ];

  // 语言切换菜单
  const languageMenuItems = [
    {
      key: 'zh',
      label: '中文',
      onClick: () => handleLanguageChange('zh'),
    },
    {
      key: 'en',
      label: 'English',
      onClick: () => handleLanguageChange('en'),
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleLanguageChange = (lang: 'zh' | 'en') => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
  };

  // 处理登出
  const handleLogout = async () => {
    try {      
      await apiClient.logout();
      message.success('登出成功');
      navigate('/login', { replace: true });
    } catch (error: any) {
      console.error('登出失败:', error);
      message.error('登出失败');
    }
  };

  // 用户下拉菜单
  const userMenuItems = [
    {
      key: 'profile',
      label: '个人信息',
      icon: <UserOutlined />,
      onClick: () => {
        // TODO: 跳转到个人信息页面
        message.info('个人信息功能待实现');
      },
    },
    {
      key: 'logout',
      label: '登出',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  // 嵌入模式下完全隐藏头部（根目录或非白名单页面）
  if (shouldHideHeader) {
    return (
      <AntLayout className="h-screen flex !flex-col">
        <Content className="!w-full flex-1 flex flex-col min-h-0">
          <div className="bg-white flex-1 flex flex-col min-h-0">
            <Outlet />
          </div>
        </Content>
      </AntLayout>
    );
  }

  return (
    <AntLayout className="min-h-screen flex !flex-col">
      {/* 嵌入模式头部：高度更低，仅显示返回按钮和标题 */}
      {isEmbedMode && shouldShowEmbedHeader ? (
        <Header
          className="!bg-white border-0 border-solid !border-b !border-gray-200 !px-4 flex items-center justify-between"
          style={{
            height: 40,
            lineHeight: '40px',
            minHeight: 40,
            padding: '0 16px',
          }}
        >
          {/* 左侧返回按钮 */}
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center w-7 h-7 rounded bg-[#CFEAFE] hover:bg-[#B0D9FE] transition-colors border-0 cursor-pointer"
            title="返回上一页"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 17 16"
              fill="none"
            >
              <path
                d="M8.08229 3.52869C8.34264 3.26834 8.7653 3.26834 9.02565 3.52869C9.28568 3.78894 9.2856 4.21076 9.02565 4.47107L6.16628 7.33044H12.554C12.9221 7.33047 13.221 7.62926 13.221 7.99744C13.2209 8.36559 12.9221 8.6644 12.554 8.66443H6.16042L9.02565 11.5287C9.28568 11.7889 9.2856 12.2108 9.02565 12.4711C8.7653 12.7314 8.34264 12.7314 8.08229 12.4711L4.08229 8.47107C3.91976 8.30835 3.85801 8.08245 3.8987 7.87244C3.92544 7.73135 3.99738 7.60658 4.09792 7.51208L8.08229 3.52869Z"
                fill="#129BFE"
              />
            </svg>
          </button>

          {/* 中间标题 */}
          <div className="flex-1 flex justify-center">
            <h2 className="text-sm font-medium text-gray-800 truncate max-w-md m-0">
              {currentOntology?.name || '编辑器'}
            </h2>
          </div>

          {/* 右侧占位，保持标题居中 */}
          <div className="w-7"></div>
        </Header>
      ) : (
        /* 正常模式头部 */
        <Header className="!bg-white border-0 border-solid !border-b !border-gray-200 !px-6 flex-between">
          <div className="flex items-center">
            <div className="flex items-center">
              <div
                className="text-xl font-bold text-blue-600 cursor-pointer hover:text-blue-700 transition-colors"
                onClick={handleGoHome}
              >
                INTELLi X
              </div>
              {shouldShowBackButton && (
                <>
                  <div className="w-px h-6 bg-gray-300 mx-4"></div>
                  <button
                    onClick={handleGoBack}
                    className="flex items-center justify-center w-8 h-8 rounded bg-[#CFEAFE] hover:bg-[#B0D9FE] transition-colors border-0 cursor-pointer"
                    title="返回上一页"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="17"
                      height="16"
                      viewBox="0 0 17 16"
                      fill="none"
                    >
                      <path
                        d="M8.08229 3.52869C8.34264 3.26834 8.7653 3.26834 9.02565 3.52869C9.28568 3.78894 9.2856 4.21076 9.02565 4.47107L6.16628 7.33044H12.554C12.9221 7.33047 13.221 7.62926 13.221 7.99744C13.2209 8.36559 12.9221 8.6644 12.554 8.66443H6.16042L9.02565 11.5287C9.28568 11.7889 9.2856 12.2108 9.02565 12.4711C8.7653 12.7314 8.34264 12.7314 8.08229 12.4711L4.08229 8.47107C3.91976 8.30835 3.85801 8.08245 3.8987 7.87244C3.92544 7.73135 3.99738 7.60658 4.09792 7.51208L8.08229 3.52869Z"
                        fill="#129BFE"
                      />
                    </svg>
                  </button>
                </>
              )}
            </div>
            {/* <Menu
              mode="horizontal"
              selectedKeys={[location.pathname]}
              items={menuItems}
              onClick={handleMenuClick}
              className="!border-none !bg-transparent flex-1"
            /> */}
          </div>

          {/* 中间标题显示 */}
          {currentOntology?.name &&
            (location.pathname === '/opm-editor' ||
              location.pathname === '/opm-editor-legacy') && (
              <div className="flex-1 flex justify-center">
                <h2 className="text-lg font-semibold text-gray-800 truncate max-w-md">
                  {currentOntology.name}
                </h2>
              </div>
            )}

          <Space>
            {/* 语言切换 */}
            {/* <Dropdown menu={{ items: languageMenuItems }} placement="bottomRight">
              <Button type="text" icon={<GlobalOutlined />}>
                {language === 'zh' ? '中文' : 'English'}
              </Button>
            </Dropdown> */}

            {/* 用户信息 */}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 px-3 py-1 rounded">
                <Avatar size="small" icon={<UserOutlined />} />
                <span className="text-gray-700">
                  {currentUser?.username || '用户'}
                </span>
              </div>
            </Dropdown>
          </Space>
        </Header>
      )}

      <Content className="!w-full flex-1 flex flex-col">
        <div
          className={`bg-white flex-1 flex flex-col${!isEmbedMode ? ' !rounded-lg !shadow-sm' : ''}`}
        >
          <Outlet />
        </div>
      </Content>

      {/* Footer暂时隐藏 */}
      {/* <Footer className="!text-center !bg-gray-50">INTELLiX ©2025</Footer> */}
    </AntLayout>
  );
};

export default Layout;
